import {
  useMutation,
  type MutationFunctionContext,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { createIdempotencyKey } from '@/lib/api';
import { type MutationLogMetadata } from '@/lib/sanitize-mutation-metadata';
import {
  resolveMutationError,
  type MutationErrorDescriptor,
} from '@/src/client/error-handling/resolve-mutation-error';
import { logMutationEvent } from '@/src/client/logging/mutation-logger';

export type MetadataResolver<TVariables> =
  | MutationLogMetadata
  | ((variables: TVariables) => MutationLogMetadata);

export type IdempotentMutationContext = {
  idempotencyKey: string;
};

export interface UseIdempotentMutationOptions<
  TData,
  TError,
  TVariables,
  TContext,
> extends Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationFn' | 'onMutate' | 'onSuccess' | 'onError' | 'onSettled'
  > {
  metadata?: MetadataResolver<TVariables>;
  getErrorMessage?: (error: TError) => string | null | undefined;
  getErrorMessageKey?: (
    error: TError
  ) => MutationErrorDescriptor | null | undefined;
  translateErrorKey?: (key: string, values?: Record<string, unknown>) => string;
  defaultErrorMessageKey?: string;
  onMutate?: (
    variables: TVariables,
    context: MutationFunctionContext
  ) => TContext | Promise<TContext> | void;
  onSuccess?: (
    data: TData,
    variables: TVariables,
    onMutateResult: TContext,
    context: MutationFunctionContext
  ) => unknown | Promise<unknown>;
  onError?: (
    error: TError,
    variables: TVariables,
    onMutateResult: TContext | undefined,
    context: MutationFunctionContext
  ) => unknown | Promise<unknown>;
  onSettled?: (
    data: TData | undefined,
    error: TError | null,
    variables: TVariables,
    onMutateResult: TContext | undefined,
    context: MutationFunctionContext
  ) => unknown | Promise<unknown>;
}

export type UseIdempotentMutationResult<TData, TError, TVariables, TContext> =
  Omit<UseMutationResult<TData, TError, TVariables, TContext>, 'reset'> & {
    idempotencyKey: string;
    errorMessage: string | null;
    reset: () => void;
  };

export function useIdempotentMutation<
  TData,
  TVariables,
  TError = unknown,
  TContext = unknown,
>(
  mutationFn: (
    variables: TVariables,
    context: IdempotentMutationContext
  ) => Promise<TData>,
  options: UseIdempotentMutationOptions<
    TData,
    TError,
    TVariables,
    TContext
  > = {}
): UseIdempotentMutationResult<TData, TError, TVariables, TContext> {
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() =>
    createIdempotencyKey()
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mergeMetadataWithIdempotencyKey = useCallback(
    (metadata?: MutationLogMetadata): MutationLogMetadata => ({
      ...(metadata ?? {}),
      idempotencyKey,
    }),
    [idempotencyKey]
  );

  const {
    metadata,
    getErrorMessage,
    getErrorMessageKey,
    translateErrorKey,
    defaultErrorMessageKey = 'errors.generic.unexpectedError',
    onMutate: userOnMutate,
    onSuccess: userOnSuccess,
    onError: userOnError,
    onSettled: userOnSettled,
    ...restOptions
  } = options;

  const resolveMetadata = useCallback(
    (variables: TVariables): MutationLogMetadata | undefined => {
      if (!metadata) {
        return undefined;
      }

      return typeof metadata === 'function' ? metadata(variables) : metadata;
    },
    [metadata]
  );

  const mutation = useMutation<TData, TError, TVariables, TContext>({
    ...restOptions,
    mutationFn: variables => mutationFn(variables, { idempotencyKey }),
    onMutate: (variables, mutationContext) => {
      setErrorMessage(null);
      const metadata = resolveMetadata(variables);
      logMutationEvent('start', {
        metadata: mergeMetadataWithIdempotencyKey(metadata),
      });
      const result = userOnMutate?.(variables, mutationContext);
      return (
        (result as TContext | Promise<TContext>) ??
        (undefined as unknown as TContext)
      );
    },
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      const metadata = resolveMetadata(variables);
      logMutationEvent('success', {
        metadata: mergeMetadataWithIdempotencyKey(metadata),
      });
      setErrorMessage(null);
      userOnSuccess?.(data, variables, onMutateResult, mutationContext);
      setIdempotencyKey(createIdempotencyKey());
    },
    onError: (error, variables, onMutateResult, mutationContext) => {
      const metadata = resolveMetadata(variables);
      const resolved = resolveMutationError(error, {
        defaultKey: defaultErrorMessageKey,
      });
      const descriptor = getErrorMessageKey?.(error) ?? resolved.descriptor;

      let computedMessage = getErrorMessage?.(error) ?? null;
      if (!computedMessage) {
        if (translateErrorKey) {
          try {
            computedMessage = translateErrorKey(
              descriptor.key,
              descriptor.values
            );
          } catch {
            logMutationEvent('translation-error', {
              metadata: mergeMetadataWithIdempotencyKey(metadata),
              ...(resolved.code && { code: resolved.code }),
            });
            computedMessage = descriptor.key;
          }
        } else {
          computedMessage = descriptor.key;
        }
      }
      setErrorMessage(computedMessage);
      logMutationEvent('error', {
        metadata: mergeMetadataWithIdempotencyKey(metadata),
        ...(resolved.code && { code: resolved.code }),
        error,
      });
      userOnError?.(error, variables, onMutateResult, mutationContext);
    },
    onSettled: (data, error, variables, onMutateResult, mutationContext) => {
      userOnSettled?.(data, error, variables, onMutateResult, mutationContext);
    },
  });

  const reset = useCallback(() => {
    mutation.reset();
    setErrorMessage(null);
    setIdempotencyKey(createIdempotencyKey());
  }, [mutation]);

  return useMemo(
    () => ({
      ...mutation,
      reset,
      idempotencyKey,
      errorMessage,
    }),
    [mutation, reset, idempotencyKey, errorMessage]
  );
}
